import React, { useEffect, useMemo, useRef, useState } from "react";
import CreatableSelect from "react-select/creatable";
import * as api from "./electricalApi";
import { ElectricalQuestionPhotos } from "./ElectricalPhotos";
import {
  STATUS_OPTIONS, CRITICALITY_OPTIONS, CRITICALITY_CLASS,
} from "./ElectricalQuestionCard";

/**
 * Area/Category, as an autocomplete combobox rather than a native <select> -
 * react-select's own menu is ordinary divs, so a long area/category name
 * wraps instead of truncating, and typing filters the list instead of
 * requiring a scroll. Picking "Create …" (any text with no matching option)
 * is how a brand new area/category gets added - there is no separate
 * sentinel option or free-text box to switch into any more.
 */
const comboStyles = {
  container: (base) => ({ ...base, width: "100%" }),
  control: (base) => ({ ...base, minHeight: 44, fontSize: 13, borderColor: "var(--rule)" }),
  valueContainer: (base) => ({ ...base, whiteSpace: "normal" }),
  singleValue: (base) => ({ ...base, whiteSpace: "normal", wordBreak: "break-word" }),
  placeholder: (base) => ({ ...base, fontSize: 13 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, minWidth: "100%" }),
  option: (base) => ({ ...base, whiteSpace: "normal", wordBreak: "break-word", fontSize: 13 }),
};

/**
 * Off-checklist observations. Modeled on Csa/CsaExtras.js, restyled to read
 * like a checkpoint card (ElectricalQuestionCard.js) rather than a plain
 * list - same Status buttons, same criticality badge styling, same photo
 * strip - because this is the same kind of finding, just one the checklist
 * itself does not ask about.
 *
 * Photos are saved the same way a checkpoint's are - through
 * ElectricalQuestionPhotos, capturePhoto/unlinkPhoto and elec_photo_link -
 * rather than a bespoke upload of their own. elec_photo_link's
 * checkpoint_code carries no foreign key to elec_checkpoint (see
 * 01_elec_schema.sql), so it is free to hold any unique string; an extra
 * observation is given a synthetic one, `extraCode()` below, once it has an
 * id to make unique from. That is also why photos are unavailable until
 * after the first save - there is nothing yet for one to attach to.
 *
 * Status/Observation/Recommendation/Legal reference live on the card
 * itself, not in the add/edit dialog - exactly where a real checkpoint
 * keeps them on the assessment page. Saving one writes through
 * api.saveAnswer, the same elec_answer row a real checkpoint's own
 * Status/Observation/Recommendation write to, keyed on this observation's
 * synthetic checkpoint_code - and through api.updateExtra in the same
 * action, since elec_extra_observation (what this screen's own list reads)
 * carries its own copy of each field too.
 *
 * The one real difference from a checkpoint: criticality is a button group
 * in the add/edit dialog, not a read-only badge - the auditor is the one
 * deciding how severe their own finding is, since there is no framework row
 * to fix it for them.
 *
 * These print in the report but never score. Letting them into the
 * denominator would make two sites incomparable: a site whose auditor
 * noticed more would look worse than one whose auditor noticed less.
 */

/** A stable, unique "checkpoint code" for an extra observation's own photos. */
const extraCode = (id) => `X${id}`;

export default function ElectricalExtras({ auditId, readOnly, framework, photos, onPhotosChanged, flash }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    let live = true;
    api
      .listExtras(auditId)
      .then((r) => live && setRows(r || []))
      .catch((e) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [auditId]);

  /*
   * Area/Category options for the add/edit form - the framework's own list,
   * plus any area/category an earlier off-checklist observation already
   * typed in fresh. Without this, a brand new area/category only ever
   * exists as free text on whichever row first introduced it - every
   * observation added after it would see "+ Add a new area/category…" and
   * have to retype the exact same name again instead of picking it.
   */
  const areas = useMemo(() => {
    const base = (framework && framework.areas) || [];
    const merged = base.map((a) => ({ ...a, categories: [...(a.categories || [])] }));
    const byName = new Map(merged.map((a) => [a.area, a]));

    (rows || []).forEach((x) => {
      if (!x.area) return;
      let a = byName.get(x.area);
      if (!a) {
        a = { area: x.area, area_no: x.area_no ?? null, categories: [] };
        byName.set(x.area, a);
        merged.push(a);
      }
      if (x.category && !a.categories.some((c) => c.category === x.category)) {
        a.categories.push({ category: x.category, checkpoints: [] });
      }
    });

    return merged;
  }, [framework, rows]);

  /*
   * Returns the saved row - a brand new observation has no id, and
   * therefore no photo code, until this resolves. Photos are never staged
   * in ExtraForm itself; the auditor adds them afterwards from the card
   * (ExtraCard) or by reopening Edit, once a real id exists to link them to.
   */
  const save = async (draft) => {
    let r = draft.id
      ? await api.updateExtra(auditId, draft.id, draft)
      : await api.addExtra(auditId, draft);
    if (!draft.id) {
      // Stamped now that the row has a real id to build one from - makes
      // the photo-linking relationship (see extraCode()) a fact on the row
      // itself rather than only ever recomputed client-side.
      r = await api.updateExtra(auditId, r.id, { checkpoint_code: extraCode(r.id) });
    }
    // Merged with what was actually sent, not trusted from the response
    // wholesale - the same class of gap as status/legal_reference not
    // coming back from a listing-card save (see saveField below): a
    // response that drops a field this request just set (category and
    // criticality in particular) must not erase it on screen even though
    // the write itself succeeded. `id` always comes from the server - a
    // brand new observation's `draft` has none yet.
    const saved = { ...r, ...draft, id: r.id };
    setRows((prev) =>
      draft.id ? prev.map((x) => (x.id === saved.id ? saved : x)) : [...prev, saved]
    );
    setError(null);
    if (flash) flash("saved", "Saved", 1200);

    // The dialog is still open for an instant after this returns - ExtraForm
    // closes it right after onSave resolves - so the card is not on screen
    // yet the moment this runs. Same pattern as ElectricalWriteUp's onJump:
    // a short delay for that paint, then scroll to it.
    const code = saved.checkpoint_code || extraCode(saved.id);
    setTimeout(() => {
      const el = document.getElementById("q-" + code);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);

    return saved;
  };

  /*
   * Status/Observation/Recommendation/Legal reference each save through two
   * requests: updateExtra, since this screen's own listing reads
   * elec_extra_observation (and is this function's source of truth); and
   * saveAnswer alongside it, mirroring the write onto elec_answer by
   * treating the observation as a checkpoint identified by its own
   * synthetic checkpoint_code, so the report's drafting tools see it too.
   *
   * The two are independent on purpose, not chained - saveAnswer is the one
   * path ever exercising a checkpoint_code with no framework row behind it,
   * so a rejection there must not stop updateExtra from running (awaiting
   * saveAnswer first and bailing out on its own error, as this used to do,
   * silently dropped every save the moment the mirror write failed - which
   * is exactly what made Status look broken on this screen while the same
   * button worked fine on the assessment page).
   *
   * Optimistic, like the assessment page's own write(): the button or box
   * must respond under the tap, not after a round trip. A failed
   * updateExtra puts the row back the way it was and surfaces the error; a
   * failed saveAnswer is swallowed; it mirrors elec_answer and is not what
   * this screen itself reads.
   *
   * The response is merged into the row, not swapped in wholesale - some
   * fields (status, legal_reference) were added to elec_extra_observation
   * after this route's response shape was first built, and a response that
   * does not carry one back would otherwise overwrite the very value this
   * call just wrote with nothing, undoing the save on screen even though
   * the request itself succeeded. The field this call wrote is kept as the
   * value just sent no matter what the response does or doesn't echo.
   */
  const saveField = async (x, field, value) => {
    const code = x.checkpoint_code || extraCode(x.id);

    setRows((prev) => prev.map((row) => (row.id === x.id ? { ...row, [field]: value } : row)));
    if (flash) flash("saving", "Saving…");
    api.saveAnswer(auditId, { checkpoint_code: code, [field]: value }).catch(() => {});

    try {
      const r = await api.updateExtra(auditId, x.id, { [field]: value });
      setRows((prev) =>
        prev.map((row) => (row.id === x.id ? { ...row, ...r, [field]: value } : row))
      );
      setError(null);
      if (flash) flash("saved", "Saved", 1200);
    } catch (e) {
      setRows((prev) => prev.map((row) => (row.id === x.id ? x : row)));
      setError(e.message);
      if (flash) flash("error", e.message, 6000);
    }
  };

  const remove = async (id) => {
    try {
      await api.deleteExtra(auditId, id);
      setRows((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      setError(e.message);
    }
  };

  /*
   * Re-reads the audit before opening the editor rather than trusting
   * whatever is already sitting in `rows` - the list here can go stale (a
   * photo attached from the assessment screen, or an edit from another
   * device) between the initial load and the moment Edit is actually
   * pressed. Falls back to the row already in hand if the refresh fails, so
   * one bad request does not block editing altogether.
   */
  const startEdit = async (x) => {
    try {
      const data = await api.getAudit(auditId);
      const fresh = data?.extras || [];
      setRows(fresh);
      setEditing(fresh.find((r) => r.id === x.id) || x);
    } catch (e) {
      setEditing(x);
    }
  };

  if (!rows) return <div className="cap-loading">Loading…</div>;

  return (
    <div className="cap-writeup">
      <div className="wu-head">
        <div>
          <h2>Other checkpoints</h2>
          <p>
            Things worth reporting that the checklist does not ask about.
            These print in the report and never affect the score.
          </p>
        </div>
        <button
          type="button"
          className="cap-btn"
          disabled={readOnly}
          onClick={() =>
            setEditing({ title: "", area: "", category: "", criticality: "" })
          }
        >
          Add an checkpoint
        </button>
      </div>

      {error ? <p className="wu-error">{error}</p> : null}

      {rows.length === 0 && !editing ? (
        <div className="wu-empty">
          <h2>Nothing recorded</h2>
          <p>
            Most audits have none. Add one only when you saw something worth
            telling the client that no checkpoint covers.
          </p>
        </div>
      ) : null}

      {rows.map((x) => (
        <ExtraCard
          key={x.id}
          extra={x}
          auditId={auditId}
          photos={photos}
          onPhotosChanged={onPhotosChanged}
          readOnly={readOnly}
          onSaveField={(field, value) => saveField(x, field, value)}
          onEdit={() => startEdit(x)}
          onDelete={() => remove(x.id)}
        />
      ))}

      {editing ? (
        <ExtraForm
          // Keyed on the observation being edited so React mounts a fresh
          // instance (and runs useState's initializers again) whenever
          // `editing` points at a different row - otherwise a reused
          // instance keeps whatever status/criticality/category its first
          // mount saw, even after `draft` changes underneath it.
          key={editing.id || "new"}
          draft={editing}
          areas={areas}
          onCancel={() => setEditing(null)}
          onSave={save}
        />
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */

const critClassFor = (c) => CRITICALITY_CLASS[String(c || "").toLowerCase()] || "a-na";

/**
 * The saved observation, same card shape ElectricalQuestionCard.js uses -
 * Status/Observation/Recommendation/Legal reference are live here, not in
 * the add/edit dialog, exactly where a real checkpoint keeps them.
 */
function ExtraCard({ extra: x, auditId, photos, onPhotosChanged, readOnly, onSaveField, onEdit, onDelete }) {
  const cls = ["q", x.status ? "a-" + String(x.status).toLowerCase().replace(/[^a-z]/g, "") : ""]
    .filter(Boolean)
    .join(" ");
  const status = String(x.status || "").toLowerCase();
  console.log('extra',x);
  return (
    <article className={cls} id={"q-" + (x.checkpoint_code || extraCode(x.id))}>
      <div className="q-code">{x.checkpoint_code}</div>
      <div className="q-text">{x.title}</div>

      {x.criticality ? (
        <span className={"q-evidence " + critClassFor(x.criticality)} title="Set by the auditor">
          {x.criticality}
        </span>
      ) : null}

      <div className="q-answers">
        {STATUS_OPTIONS.map((o) => (
          <button
            key={o}
            type="button"
            disabled={readOnly}
            className={"ans" + (status === o.toLowerCase() ? " on-" + o.toLowerCase() : "")}
            aria-pressed={status === o.toLowerCase()}
            onClick={() => onSaveField("status", x.status === o ? null : o)}
          >
            {o}
          </button>
        ))}
      </div>

      {(x.area || x.category) ? (
        <div className="xo-where">
          {x.area ? <strong>{x.area}</strong> : null}
          {x.area && x.category ? " · " : null}
          {x.category}
        </div>
      ) : null}

      <ExtraTextField
        id={"xo-obs-" + x.id}
        label="Checkpoint"
        required
        value={x.observation}
        disabled={readOnly}
        onSave={(v) => onSaveField("observation", v)}
      />
      <ExtraTextField
        id={"xo-rec-" + x.id}
        label="Recommendation"
        // Not required for N/A - there is nothing to recommend fixing on
        // something that does not apply. Same exception the assessment
        // page's checkpoint cards make (needsRecommendation).
        required={String(x.status || "").toUpperCase() !== "NA"}
        value={x.recommendation}
        disabled={readOnly}
        onSave={(v) => onSaveField("recommendation", v)}
      />
      <ExtraTextField
        id={"xo-legal-" + x.id}
        label="Legal reference"
        value={x.legal_reference}
        disabled={readOnly}
        onSave={(v) => onSaveField("legal_reference", v)}
      />

      <ElectricalQuestionPhotos
        auditId={auditId}
        checkpointCode={x.checkpoint_code || extraCode(x.id)}
        photos={photos}
        disabled={readOnly}
        onChange={onPhotosChanged}
      />

      <div className="scope-actions">
        <button type="button" className="small-btn" disabled={readOnly} onClick={onEdit}>
          Edit
        </button>
        <button type="button" className="small-btn warn" disabled={readOnly} onClick={onDelete}>
          Delete
        </button>
      </div>
    </article>
  );
}

/**
 * One free-typed field, saved on blur - the Observation/Recommendation/
 * Legal reference equivalent of ElectricalQuestionCard's FieldPicker, minus
 * the "choose the checklist's own template text" option, since an
 * off-checklist observation has no framework row to offer one from.
 *
 * Adopts the server's value only while the auditor is not mid-sentence
 * (the `dirty` ref), same reasoning as FieldPicker: without it, a listing
 * refresh landing between keystrokes would snap the box back and eat the
 * typing.
 */
function ExtraTextField({ id, label, value, disabled, required, onSave }) {
  const [draft, setDraft] = useState(value || "");
  const dirty = useRef(false);

  useEffect(() => {
    if (dirty.current) return;
    setDraft(value || "");
  }, [value]);

  const handleChange = (e) => {
    setDraft(e.target.value);
    dirty.current = true;
  };

  const flush = () => {
    if (!dirty.current) return;
    dirty.current = false;
    onSave(draft);
  };

  const needsNote = required && !draft.trim();

  return (
    <div className={"q-cmt" + (needsNote ? " need" : "")}>
      <label htmlFor={id}>
        {label}
        {required ? <span className="req"> — required</span> : null}
      </label>
      <input
        id={id}
        type="text"
        className="q-comment"
        value={draft}
        disabled={disabled}
        onChange={handleChange}
        onBlur={flush}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */

function ExtraForm({ draft, areas, onCancel, onSave }) {
  const [form, setForm] = useState({
    id: draft.id,
    title: draft.title || "",
    area: draft.area || "",
    category: draft.category || "",
    criticality: draft.criticality || "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const ready = form.title.trim() && form.area.trim() && form.category.trim();

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const doSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      // A typed-fresh area/category may still name one that already exists
      // under a different case or stray whitespace (e.g. "tr-signage" vs
      // "TR-Signage") - matched case-insensitively against the checklist so
      // the save reuses that exact name instead of creating a second,
      // differently-spelled one the UI would then treat as distinct.
      const typedArea = form.area.trim();
      const matchedArea = areas.find(
        (a) => a.area.trim().toLowerCase() === typedArea.toLowerCase()
      );
      const areaName = matchedArea ? matchedArea.area : typedArea;
      // The area's own number from the framework, alongside its name - null
      // when the area is genuinely new, since it has no number of its own yet.
      const area_no = matchedArea ? matchedArea.area_no ?? null : null;

      const typedCategory = form.category.trim();
      let matchedCategory = null;
      areas.forEach((a) => {
        (a.categories || []).forEach((c) => {
          if (!matchedCategory && c.category.trim().toLowerCase() === typedCategory.toLowerCase()) {
            matchedCategory = c.category;
          }
        });
      });
      const categoryName = matchedCategory || typedCategory;

      // Status/Observation/Recommendation/Legal reference are not part of
      // this form any more - they save from the card itself (see
      // saveField/ExtraTextField above) - so they are left out of this
      // payload entirely rather than sent as null, which would otherwise
      // wipe out whatever the auditor already set there.
      await onSave({
        ...form,
        area: areaName || null,
        area_no,
        category: categoryName || null,
        criticality: form.criticality || null,
      });
      onCancel();
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  /*
   * Every category under the chosen area - empty, not "every category in
   * the checklist", until an area is picked. Category names recur across
   * areas (e.g. "TR-Signage" under more than one), so without an area
   * first there is no way to tell which area a picked name would actually
   * belong to.
   */
  const categoryOptions = useMemo(() => {
    if (!form.area) return [];
    const names = new Set();
    areas.forEach((a) => {
      if (a.area !== form.area) return;
      (a.categories || []).forEach((cat) => names.add(cat.category));
    });
    return [...names];
  }, [areas, form.area]);

  return (
    <>
      <div className="elec-pad-scrim" onClick={onCancel} />
      <div className="scope-dialog wide" role="dialog" aria-modal="true">
        <h3>{form.id ? "Edit observation" : "New observation"}</h3>

        <label htmlFor="x-title">Checkpoint</label>
        <input id="x-title" type="text" value={form.title} onChange={set("title")} maxLength={200} />

        {/* Unlike a checkpoint's badge, this one is the auditor's own call -
            there is no framework row to fix it for an off-checklist finding.
            Status/Observation/Recommendation/Legal reference live on the
            card itself once this is saved, not here. */}
        <label>Criticality</label>
        <div className="q-answers">
          {CRITICALITY_OPTIONS.map((o) => (
            <button
              key={o}
              type="button"
              className={
                "ans crit-btn" +
                (form.criticality === o ? " on-" + critClassFor(o).replace("a-", "") : "")
              }
              onClick={() => setForm((f) => ({ ...f, criticality: f.criticality === o ? "" : o }))}
            >
              {o}
            </button>
          ))}
        </div>

        <label htmlFor="x-area">
          Area<span className="req"> — required</span>
        </label>
        <CreatableSelect
          inputId="x-area"
          classNamePrefix="xo-combo"
          placeholder="Choose or type a new area…"
          formatCreateLabel={(input) => `+ Add a new area "${input}"`}
          options={areas.map((a) => ({ value: a.area, label: a.area }))}
          value={form.area ? { value: form.area, label: form.area } : null}
          onChange={(opt) => setForm((f) => ({ ...f, area: opt ? opt.value : "" }))}
          isClearable
          styles={comboStyles}
          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        />

        <label htmlFor="x-category">
          Category<span className="req"> — required</span>
        </label>
        <CreatableSelect
          inputId="x-category"
          classNamePrefix="xo-combo"
          placeholder={form.area ? "Choose or type a new category…" : "Choose an area first"}
          isDisabled={!form.area}
          formatCreateLabel={(input) => `+ Add a new category "${input}"`}
          options={categoryOptions.map((c) => ({ value: c, label: c }))}
          value={form.category ? { value: form.category, label: form.category } : null}
          onChange={(opt) => setForm((f) => ({ ...f, category: opt ? opt.value : "" }))}
          isClearable
          styles={comboStyles}
          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        />

        {!form.id ? (
          // Same as a checkpoint on the assessment page: photos live on the
          // card, not in this dialog. A brand new observation has no id yet
          // to link one to, so the auditor saves first, then attaches
          // photos from the card in the list.
          <p className="na-hint">Save the observation first, then add photos from the list.</p>
        ) : null}

        {saveError ? <p className="wu-error">{saveError}</p> : null}

        <div className="scope-actions">
          <button type="button" className="small-btn" onClick={onCancel} disabled={saving}>
            {form.id ? "Close" : "Cancel"}
          </button>
          <button type="button" className="cap-btn" disabled={!ready || saving} onClick={doSave}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </>
  );
}
